/**
 * 科技蓝模板 - 分类列表页
 */

import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getListings, getSiteCategories, ListingItem, FrontendCategory } from "@/utils/api";
import TechBlueNavbar from "../components/Navbar";
import TechBlueFooter from "../components/Footer";

export default function CategoryPage() {
  const [searchParams] = useSearchParams();

  const [listings, setListings] = useState<ListingItem[]>([]);
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const activeCat = searchParams.get("cat") || "";
  const searchQuery = searchParams.get("search") || "";
  const PAGE_SIZE = 12;

  useEffect(() => {
    getSiteCategories().then(d => setCategories(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
    const params: any = { page: 1, page_size: PAGE_SIZE };
    if (activeCat) {
      const cat = categories.find(c => c.slug === activeCat);
      if (cat) params.category_id = cat.id;
    }
    if (searchQuery) params.q = searchQuery;
    getListings(params).then(d => {
      setListings(d.list || []);
      setTotal(d.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [activeCat, searchQuery, categories]);

  useEffect(() => {
    if (currentPage === 1) return;
    setLoading(true);
    const params: any = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCat) {
      const cat = categories.find(c => c.slug === activeCat);
      if (cat) params.category_id = cat.id;
    }
    if (searchQuery) params.q = searchQuery;
    getListings(params).then(d => {
      setListings(d.list || []);
      setTotal(d.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50">
      <TechBlueNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* 分类标签条 */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border border-gray-100">
          <div className="flex flex-wrap gap-2">
            <Link to="/category" className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${!activeCat ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>全部</Link>
            {categories.map((cat) => (
              <Link key={cat.id} to={`/category?cat=${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeCat === cat.slug ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {searchQuery && <div className="mb-4 text-sm text-gray-500">搜索：<span className="text-gray-900 font-medium">"{searchQuery}"</span></div>}

        {loading ? (
          <div className="text-center py-16 text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><i className="ri-inbox-line text-4xl mb-2 block" />暂无内容</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((item) => (
              <Link key={item.id} to={`/listing/${item.id}`} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:border-cyan-200 transition-all group">
                {item.images?.[0] ? (
                  <img src={item.images[0]} alt={item.title} className="w-full aspect-square object-cover" />
                ) : (
                  <div className="w-full aspect-square bg-gradient-to-br from-cyan-50 to-slate-100 flex items-center justify-center"><i className="ri-image-line text-gray-300 text-3xl" /></div>
                )}
                <div className="p-4">
                  {item.category?.name && <span className="text-xs text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded">{item.category.name}</span>}
                  <h3 className="text-sm font-semibold text-gray-900 mt-2 group-hover:text-cyan-600 transition-colors line-clamp-2">{item.title}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.content}</p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                    <span><i className="ri-time-line mr-0.5" />{item.created_at}</span>
                    <span><i className="ri-eye-line mr-0.5" />{item.view_count}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* 分页 */}
        {!loading && totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:border-cyan-300 hover:text-cyan-600 disabled:opacity-40 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {generatePagination(currentPage, totalPages).map((p, i) => (
              <button
                key={i}
                onClick={() => typeof p === 'number' && setCurrentPage(p)}
                disabled={typeof p !== 'number'}
                className={`w-10 h-10 flex items-center justify-center rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  currentPage === p
                    ? "bg-gray-900 text-white shadow-md"
                    : typeof p === 'number'
                    ? "bg-white border border-gray-200 text-gray-600 hover:border-cyan-300 hover:text-cyan-600"
                    : "text-gray-400"
                }`}
              >
                {typeof p === 'number' ? p : '...'}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:border-cyan-300 hover:text-cyan-600 disabled:opacity-40 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <TechBlueFooter />
    </div>
  );
}

function generatePagination(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }
  return pages;
}
